import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import CheckboxFieldComponent from './form-components/CheckboxFieldComponent';
import DateFieldComponent from './form-components/DateFieldComponent';
import HeaderFieldComponent from './form-components/HeaderFieldComponent';
import HiddenFieldComponent from './form-components/HiddenFieldComponent';
import NumberFieldComponent from './form-components/NumberFieldComponent';
import RadioFieldComponent from './form-components/RadioFieldComponent';
import SelectFieldComponent from './form-components/SelectFieldComponent';
import TextFieldComponent from './form-components/TextFieldComponent';
import { formReducer, formReducerInitialState } from './formReducer';
import { SET_VALUE } from './dataToSendReducer';

interface Props {
    nodeForm: any;
    dataToSendState: any;
    dataToSendStateDispatch: any;
    required: boolean;
    index: string;
    readOnly: boolean;
    formId?: string;
}

const decodeHtmlEntities = (str: string) => {
    if (!str) return str;
    const txt = typeof window !== 'undefined' ? document.createElement('textarea') : null;
    if (!txt) return str;
    txt.innerHTML = str;
    return txt.value;
};

const GlobalNoteDialogFormField: React.FC<Props> = ({
    nodeForm,
    dataToSendStateDispatch,
    dataToSendState,
    required,
    index,
    readOnly = false,
    formId: formIdProp,
}) => {
    const [formState, formDispatch]: any = useReducer<any>(
        formReducer,
        'forms' in dataToSendState && index in dataToSendState.forms
            ? dataToSendState.forms[index].toSend
            : formReducerInitialState,
    );

    const formData = useMemo(() => JSON.parse(nodeForm.data.formData), [nodeForm.data.formData]);

    const formId = formIdProp ? formIdProp : nodeForm.data.id;
    const formName = nodeForm.data.name;

    const validateObject = (data: any) => {
        const keys = Object.keys(data || {});
        let out = false;

        keys.forEach((key) => {
            if (data[key]) out = true;
        });

        return !out;
    };

    const validateFields = useCallback(() => {
        let requiredFields: any[] = [];

        formData.forEach((item: any) => {
            if (item?.required) requiredFields.push(item.name);
        });

        let out = requiredFields.length === 0;

        if (!out) {
            out = true;
            requiredFields.forEach((key: string) => {
                if (
                    formId in formState &&
                    key in formState[formId] &&
                    (formState[formId][key] !== null && typeof formState[formId][key] === 'object'
                        ? validateObject(formState[formId][key])
                        : formState[formId][key] === '')
                ) {
                    out = false;
                }
            });
        }

        return out;
    }, [formData, formId, formState]);

    useEffect(() => {
        dataToSendStateDispatch({
            type: SET_VALUE,
            payload: {
                key: 'forms',
                data: {
                    toSend: formState,
                    required: required,
                    valid: validateFields(),
                },
                index,
            },
        });
    }, [dataToSendStateDispatch, formState, index, required, validateFields]);

    const isFirstInputText = (item: any) => {
        let firstInputTextName: string | null = null;
        formData.forEach((value: any) => {
            const { type } = value;
            if ((type === 'text' || type === 'textarea' || type === 'date') && !firstInputTextName) {
                firstInputTextName = value.name;
            }
        });
        return !!firstInputTextName && item.name === firstInputTextName;
    };

    const renderComponent = (item: any, idx: number) => {
        const { type, name, label } = item;

        const key: string = `${name}${idx}`;
        const commonProps: any = {
            formDispatch,
            key,
            formId,
            formName,
            value: formId in formState && name in formState[formId] ? formState[formId][name] : '',
            item: { ...item, label: decodeHtmlEntities(item.label) },
            dataTestSc: name,
            autoFocus: isFirstInputText(item),
            readOnly,
        };

        if (label === 'hidden') {
            return <HiddenFieldComponent {...commonProps} />;
        }

        switch (type) {
            case 'header': {
                return <HeaderFieldComponent item={item} key={key} />;
            }
            case 'text':
            case 'textarea': {
                return <TextFieldComponent {...commonProps} />;
            }
            case 'date': {
                return <DateFieldComponent {...commonProps} />;
            }
            case 'number': {
                return <NumberFieldComponent {...commonProps} />;
            }
            case 'hidden': {
                return <HiddenFieldComponent {...commonProps} />;
            }
            case 'select': {
                return (
                    <SelectFieldComponent
                        {...commonProps}
                        value={formId in formState && name in formState[formId] ? formState[formId][name] : null}
                    />
                );
            }
            case 'radio-group': {
                return (
                    <RadioFieldComponent
                        {...commonProps}
                        value={formId in formState && name in formState[formId] ? formState[formId][name] : null}
                    />
                );
            }
            case 'checkbox-group': {
                return (
                    <CheckboxFieldComponent
                        {...commonProps}
                        value={formId in formState && name in formState[formId] ? formState[formId][name] : null}
                    />
                );
            }
            default: {
                return null;
            }
        }
    };

    return <>{formData.map((item: any, idx: number) => renderComponent(item, idx))}</>;
};

export default GlobalNoteDialogFormField;
